import React, { useState } from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Calculator, IndianRupee } from "lucide-react";

const cityPricing = {
  bangalore: 1.0,
  hyderabad: 0.95,
  chennai: 0.93,
  pune: 1.02,
  mumbai: 1.15,
  delhi: 1.08,
};

const basePricePerSqFt = 1500; // Base price per sq ft

export const CostCalculatorSection = ({ onContactClick }) => {
  const [bhkType, setBhkType] = useState("3");
  const [city, setCity] = useState("bangalore");
  const [area, setArea] = useState([1000]);
  const [scope, setScope] = useState("complete");

  const calculateEstimate = () => {
    const bhkMultiplier = {
      "1": 0.7,
      "2": 0.85,
      "3": 1.0,
      "4": 1.2,
      "5": 1.5,
      villa: 2.0,
    };

    const scopeMultiplier = {
      modular: 0.6,
      complete: 1.0,
      luxury: 1.5,
    };

    const estimate =
      area[0] *
      basePricePerSqFt *
      (bhkMultiplier[bhkType] || 1) *
      (scopeMultiplier[scope] || 1) *
      (cityPricing[city] || 1);

    return Math.round(estimate);
  };

  const estimate = calculateEstimate();
  const minEstimate = Math.round(estimate * 0.85);
  const maxEstimate = Math.round(estimate * 1.15);

  return (
    <section className="section bg-background" data-testid="cost-calc-section">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <p className="uppercase tracking-[0.3em] text-[11px] text-muted-foreground mb-4">
            Estimate Your Budget
          </p>
          <h2 className="font-display text-4xl md:text-5xl font-semibold text-foreground">
            Cost Calculator
          </h2>
          <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">
            Get an approximate estimate for your interior design project based on
            your requirements.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-5 gap-8 max-w-6xl mx-auto" data-testid="cost-calc-form">
          {/* Calculator Form */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="md:col-span-3"
          >
            <Card className="border-[hsl(var(--border))]">
              <CardHeader>
                <CardTitle className="font-serif-alt flex items-center gap-2">
                  <Calculator className="w-5 h-5 text-[hsl(var(--brand-gold))]" />
                  Project Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* City Selection */}
                <div className="space-y-2">
                  <Label htmlFor="city" data-testid="calc-city-label">
                    City
                  </Label>
                  <Select value={city} onValueChange={setCity}>
                    <SelectTrigger id="city" data-testid="calc-city-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bangalore">Bangalore</SelectItem>
                      <SelectItem value="hyderabad">Hyderabad</SelectItem>
                      <SelectItem value="chennai">Chennai</SelectItem>
                      <SelectItem value="pune">Pune</SelectItem>
                      <SelectItem value="mumbai">Mumbai</SelectItem>
                      <SelectItem value="delhi">Delhi</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* BHK Type */}
                <div className="space-y-2">
                  <Label htmlFor="bhk" data-testid="calc-bhk-label">
                    Property Type
                  </Label>
                  <Select value={bhkType} onValueChange={setBhkType}>
                    <SelectTrigger id="bhk" data-testid="calc-bhk-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 BHK</SelectItem>
                      <SelectItem value="2">2 BHK</SelectItem>
                      <SelectItem value="3">3 BHK</SelectItem>
                      <SelectItem value="4">4 BHK</SelectItem>
                      <SelectItem value="5">5 BHK</SelectItem>
                      <SelectItem value="villa">Villa / Independent House</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Area Slider */}
                <div className="space-y-4">
                  <Label htmlFor="area" data-testid="calc-area-label">
                    Carpet Area: <span className="font-semibold text-[hsl(var(--brand-gold))]">{area[0]} sq ft</span>
                  </Label>
                  <Slider
                    id="area"
                    value={area}
                    onValueChange={setArea}
                    min={500}
                    max={5000}
                    step={100}
                    data-testid="calc-budget-slider"
                    className="[&_[role=slider]]:bg-[hsl(var(--brand-gold))] [&_[role=slider]]:border-[hsl(var(--brand-gold))] [&_.bg-primary]:bg-[hsl(var(--brand-gold))]"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>500 sq ft</span>
                    <span>5000 sq ft</span>
                  </div>
                </div>

                {/* Scope Selection */}
                <div className="space-y-2">
                  <Label htmlFor="scope" data-testid="calc-scope-label">
                    Project Scope
                  </Label>
                  <Select value={scope} onValueChange={setScope}>
                    <SelectTrigger id="scope" data-testid="calc-scope-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="modular">Modular Interiors</SelectItem>
                      <SelectItem value="complete">Complete Home Interiors</SelectItem>
                      <SelectItem value="luxury">Premium Luxury Package</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Estimate Result */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="md:col-span-2"
          >
            <Card className="border-[hsl(var(--brand-gold))] bg-gradient-to-br from-[hsl(var(--brand-gold))]/5 to-transparent sticky top-24">
              <CardHeader>
                <CardTitle className="font-serif-alt text-center">Estimated Cost</CardTitle>
              </CardHeader>
              <CardContent className="text-center space-y-6">
                <div data-testid="calc-estimate-value">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <IndianRupee className="w-8 h-8 text-[hsl(var(--brand-gold))]" />
                    <span className="text-5xl font-display font-bold text-[hsl(var(--brand-gold))]">
                      {(estimate / 100000).toFixed(1)}
                    </span>
                    <span className="text-2xl text-muted-foreground">L</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    ₹{minEstimate.toLocaleString("en-IN")} - ₹{maxEstimate.toLocaleString("en-IN")}
                  </p>
                </div>

                <div className="space-y-2 text-sm text-muted-foreground bg-muted/30 p-4 rounded-lg">
                  <div className="flex justify-between">
                    <span>Base Cost:</span>
                    <span>₹{basePricePerSqFt}/sq ft</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Area:</span>
                    <span>{area[0]} sq ft</span>
                  </div>
                  <div className="flex justify-between">
                    <span>City:</span>
                    <span className="capitalize">{city}</span>
                  </div>
                </div>

                <Button
                  variant="gold"
                  size="lg"
                  className="w-full"
                  onClick={onContactClick}
                  data-testid="calc-submit-button"
                >
                  Get Accurate Quote
                </Button>

                <p className="text-xs text-muted-foreground">
                  *This is an approximate estimate. Final costs may vary based on
                  materials, finishes, and customization.
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default CostCalculatorSection;
